'use client';

import { ReactNode, useState } from 'react';
import { Animate } from 'react-animate-mount';

import Link from 'next/link';

import cn from '@/helpers/utils/classNames';
import { AddOutlined, RemoveOutlined } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { HeadingFour } from '../Typography/Heading';

interface AccordionItemProps {
  id: number;
  label: string;
  description: string | ReactNode;
  totalAccordions: number;
  activeAccordion: number;
  type?: 'list' | 'default';
  onClick: (id: number) => void;
  panelClassName?: string;
  panelContentClassName?: string;
}

function AccordionItem(props: AccordionItemProps) {
  const {
    onClick,
    label,
    description,
    activeAccordion,
    totalAccordions,
    id,
    panelClassName,
    panelContentClassName,
    type = 'default',
  } = props;
  const isActive = id === activeAccordion;

  const addBorderBottom = isActive || totalAccordions === 1 || id === totalAccordions - 1;

  return (
    <>
      {type === 'list' && (
        <div
          className={cn(
            'text-grayscale-90',
            {
              'border-y-2 border-y-[#f7f6f2]': addBorderBottom,
              'border-t-2 border-t-[#f7f6f2] last:border-b-2 last:border-b-[#f7f6f2]': !addBorderBottom,
            },
            panelClassName
          )}
        >
          <HeadingFour
            variant="light-gray"
            className="flex items-center justify-between p-[15px]"
            onClick={event => {
              event.preventDefault();
              onClick(id);
            }}
          >
            <span className="block flex-grow">{label}</span>
            <ArrowForwardIosIcon
              className={cn(
                'transition-all duration-300 sm:!h-4 sm:!w-4 md:!h-5 md:!w-5 xs:!h-3 xs:!w-3 xxs:!h-2 xxs:!w-2',
                {
                  'rotate-90': isActive,
                }
              )}
            />
          </HeadingFour>
        </div>
      )}
      {type == 'default' && (
        <Link
          href="#"
          className={cn(
            'flex items-center justify-between text-grayscale-90 sm:px-0 sm:py-[11px] lg:py-[14px] lg:pl-[22px] lg:pr-[12px]',
            {
              'border-y-2': addBorderBottom,
              'border-t-2 last:border-b-2': !addBorderBottom,
            }
          )}
          onClick={event => {
            event.preventDefault();
            onClick(id);
          }}
        >
          <HeadingFour
            className="font-circular-std-medium text-grayscale-95 sm:text-[16px] lg:text-lg xs:text-[16px] xxs:text-[16px]"
            variant="dark"
          >
            {label}
          </HeadingFour>
          <p className="text-primary">
            {!isActive ? (
              <AddOutlined className="sm:h-8 sm:w-8 md:h-9 md:w-9 xs:h-6 xs:w-6 xxs:h-6 xxs:w-6" />
            ) : (
              <RemoveOutlined className="sm:h-8 sm:w-8 md:h-10 md:w-10 xs:h-6 xs:w-6 xxs:h-6 xxs:w-6" />
            )}
          </p>
        </Link>
      )}

      <Animate show={isActive} type="slide" duration={300}>
        <div
          className={cn(
            'font-light text-grayscale-80',
            {
              'my-0 py-[22px] px-[22px]': isActive,
            },
            panelContentClassName
          )}
        >
          {isActive ? description : null}
        </div>
      </Animate>
    </>
  );
}

export interface AccordionProps {
  items: Array<Omit<AccordionItemProps, 'id' | 'activeAccordion' | 'onClick' | 'totalAccordions'>>;
  type?: 'list' | 'default';
  panelClassName?: string;
  panelContentClassName?: string;
  className?: string;
}

export default function Accordion(props: AccordionProps) {
  const { items, type = 'default', panelClassName, panelContentClassName, className } = props;
  const INITIALLY_ACTIVE_INDEX = -1;
  const [activeIndex, setActiveIndex] = useState(INITIALLY_ACTIVE_INDEX);
  return (
    <div className={cn({ 'border-b-2': activeIndex === items.length - 1 }, className)}>
      {items.map((item, index) => (
        <AccordionItem
          {...item}
          panelClassName={panelClassName}
          panelContentClassName={panelContentClassName}
          type={type}
          key={item.label}
          id={index}
          activeAccordion={activeIndex}
          totalAccordions={items.length}
          onClick={id => setActiveIndex(prevIndex => (prevIndex === id ? INITIALLY_ACTIVE_INDEX : id))}
        />
      ))}
    </div>
  );
}
